import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import NavigationHeader from "../../components/ui/NavigationHeader";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Icon from "../../components/AppIcon";
import {
  createRotaPlanejada,
  deleteRotaPlanejada,
  deleteRotaPlanejadaHistory,
  fetchRotaPlanejadaById,
  fetchRotaPlanejadaHistory,
  fetchRotasPlanejadasDashboard,
  fetchRotasPlanejadasRecords,
  updateRotaPlanejada,
} from "./services/planejamentoApi";
import { fetchMercosAtendimentos, fetchMercosVisitas } from "../mercos-rotas-dashboard/services/mercosApi";
import { formatBaseLabel, formatBaseList } from "../../utils/baseDisplay";

const todayISO = () => new Date().toISOString().slice(0, 10);
const startOfMonthISO = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const baseOptions = [
  { value: "ALL", label: formatBaseLabel("ALL") },
  { value: "A", label: formatBaseLabel("A") },
  { value: "B", label: formatBaseLabel("B") },
];

const cadastroBaseOptions = baseOptions.filter((option) => option.value !== "ALL");
const statusOptions = [
  { value: "", label: "Todos os status" },
  { value: "ativo", label: "Ativo" },
  { value: "inativo", label: "Inativo" },
];

const initialForm = {
  base: "A",
  dataRota: todayISO(),
  vendedorNome: "",
  nomeRoteiro: "",
  quantidadePlanejada: "",
  status: "ativo",
  observacao: "",
  clientesPlanejados: [],
};

function SummaryCard({ title, value, subtitle, icon }) {
  return (
    <div className="rounded-2xl border border-[#f1d6c6] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-[#6b3a2a]">{title}</div>
        <Icon name={icon} size={16} />
      </div>
      <div className="mt-2 text-3xl font-bold text-[#3b1d0f]">{value}</div>
      <div className="text-xs text-[#6b3a2a]">{subtitle}</div>
    </div>
  );
}

function formatPercent(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "0%";
  return `${numeric.toFixed(2).replace(".", ",")}%`;
}

function formatDateBR(value) {
  if (!value) return "---";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function formatDateTimeBR(value) {
  if (!value) return "---";
  const normalized = String(value).includes("T") ? String(value) : String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(date);
}

function getHistoryEventLabel(event) {
  if (event === "deleted") return "Excluído";
  if (event === "updated") return "Versão anterior";
  return "Atual";
}

function getChangedFieldLabel(field) {
  const labels = {
    base: "Empresa",
    dataRota: "Data",
    vendedorNome: "Vendedor",
    nomeRoteiro: "Roteiro",
    quantidadePlanejada: "Quantidade",
    status: "Status",
    observacao: "Observação",
    clientesPlanejados: "Clientes",
    deleted: "Exclusão",
  };

  return labels[field] || field;
}

function normalizeBaseValue(value) {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "A" || normalized === "B" || normalized === "ALL") return normalized;
  return "";
}

function normalizeTextKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function normalizeMercosClient(row = {}) {
  const clienteId = row?.clienteId == null ? "" : String(row.clienteId).trim();
  const clienteNome = String(row?.razaoSocial || row?.clienteNomeFantasia || row?.clienteNome || row?.descricao || "").trim();
  const clienteNomeKey = normalizeTextKey(clienteNome);
  const matchKey = clienteId ? `id:${clienteId}` : clienteNomeKey ? `nome:${clienteNomeKey}` : "";

  return {
    clienteId,
    clienteNome,
    clienteNomeKey,
    matchKey,
  };
}

function parseBulkClientLines(value) {
  return String(value || "")
    .replace(/;/g, "\n")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function stripCommonClientWords(value) {
  return String(value || "")
    .replace(/\b(ltda|me|eireli|sa|s a|epp|comercio|de|do|da|dos|das)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findBestClientSuggestion(rawName, suggestions = []) {
  const rawKey = normalizeTextKey(rawName);
  if (!rawKey) return null;

  const enriched = suggestions.map((item) => ({
    ...item,
    nameKey: normalizeTextKey(item?.clienteNome || item?.label || ""),
  }));

  const exact = enriched.find((item) => item.nameKey === rawKey);
  if (exact) return exact;

  const rawReduced = stripCommonClientWords(rawKey);
  if (rawReduced) {
    const reduced = enriched.find((item) => stripCommonClientWords(item.nameKey) === rawReduced);
    if (reduced) return reduced;
  }

  const fuzzy = enriched.filter((item) => item.nameKey && (item.nameKey.includes(rawKey) || rawKey.includes(item.nameKey)));
  if (fuzzy.length === 1) return fuzzy[0];

  return null;
}

function collectMercosRows(payload) {
  if (!payload || typeof payload !== "object") return [];

  const rows = [];
  const pushRows = (entries = [], forcedBase = "") => {
    entries.forEach((entry) => {
      rows.push({ ...entry, base: normalizeBaseValue(entry?.base || forcedBase) || forcedBase || entry?.base || "" });
    });
  };

  if (Array.isArray(payload.rows)) pushRows(payload.rows);
  if (Array.isArray(payload?.combined?.rows)) pushRows(payload.combined.rows);

  ["A", "B"].forEach((base) => {
    if (Array.isArray(payload?.bases?.[base]?.rows)) {
      pushRows(payload.bases[base].rows, base);
    }
  });

  return rows;
}

function collectMercosSellers(...payloads) {
  const map = new Map();

  const appendSeller = (seller) => {
    const name = String(
      seller?.sellerName || seller?.colaboradorNome || seller?.name || seller?.label || ""
    ).trim();
    if (!name) return;

    const sellerBase = normalizeBaseValue(seller?.base);
    const current = map.get(name) || { value: name, name, bases: new Set() };

    if (sellerBase === "A" || sellerBase === "B") current.bases.add(sellerBase);
    map.set(name, current);
  };

  const appendPayload = (payload) => {
    if (!payload || typeof payload !== "object") return;

    const directSellers = Array.isArray(payload.sellers) ? payload.sellers : [];
    const combinedSellers = Array.isArray(payload?.combined?.sellers) ? payload.combined.sellers : [];

    directSellers.forEach(appendSeller);
    combinedSellers.forEach(appendSeller);

    ["A", "B"].forEach((base) => {
      const sellers = Array.isArray(payload?.bases?.[base]?.sellers) ? payload.bases[base].sellers : [];
      sellers.forEach((seller) => appendSeller({ ...seller, base: seller?.base || base }));
    });

    collectMercosRows(payload).forEach((row) => {
      appendSeller({
        sellerName: row?.colaboradorNome || row?.sellerName || "",
        base: row?.base,
      });
    });
  };

  payloads.forEach(appendPayload);

  return Array.from(map.values())
    .map((item) => {
      const bases = Array.from(item.bases).sort();
      const baseLabel = bases.length === 0 ? "" : ` (${formatBaseList(bases)})`;
      return {
        value: item.value,
        label: `${item.name}${baseLabel}`,
        bases,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

function buildClientSuggestions(rows, { base, dataRota, vendedorNome }) {
  const normalizedBase = normalizeBaseValue(base);
  const normalizedSeller = normalizeTextKey(vendedorNome);
  const suggestions = new Map();

  rows.forEach((row) => {
    const rowBase = normalizeBaseValue(row?.base);
    const rowDate = String(row?.dataHoraCheckin || row?.dataHoraCheckout || row?.dataInicio || row?.data_evento || "").slice(0, 10);
    const sellerKey = normalizeTextKey(row?.colaboradorNome || row?.sellerName || "");
    const client = normalizeMercosClient(row);

    if (!client.clienteNome || !client.matchKey) return;
    if (normalizedBase && normalizedBase !== "ALL" && rowBase && rowBase !== normalizedBase) return;
    if (dataRota && rowDate && rowDate !== dataRota) return;
    if (normalizedSeller && sellerKey !== normalizedSeller) return;

    if (!suggestions.has(client.matchKey)) {
      suggestions.set(client.matchKey, {
        value: client.matchKey,
        label: client.clienteNome,
        clienteId: client.clienteId,
        clienteNome: client.clienteNome,
        base: rowBase || "",
        data: rowDate || "",
      });
    }
  });

  return Array.from(suggestions.values()).sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

export default function MercosRotasPlanejadasPage() {
  const [filters, setFilters] = useState({
    base: "ALL",
    de: startOfMonthISO(),
    ate: todayISO(),
    status: "ativo",
    vendedorNome: "",
  });
  const [tab, setTab] = useState("comparativo");
  const [dashboard, setDashboard] = useState({ summary: {}, rows: [] });
  const [records, setRecords] = useState([]);
  const [mercosSellers, setMercosSellers] = useState([]);
  const [mercosRows, setMercosRows] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [selectedClientSuggestion, setSelectedClientSuggestion] = useState("");
  const [manualClientName, setManualClientName] = useState("");
  const [bulkClientText, setBulkClientText] = useState("");
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkPreviewSummary, setBulkPreviewSummary] = useState({ total: 0, matched: 0, manual: 0, duplicates: 0 });
  const [historyModalRoute, setHistoryModalRoute] = useState(null);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!filters.de || !filters.ate) {
      setError("Preencha o período antes de consultar.");
      return;
    }

    if (filters.de > filters.ate) {
      setError("A data inicial não pode ser maior que a data final.");
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadingSellers(true);
    setError("");

    const mercosParams = {
      base: filters.base,
      de: filters.de,
      ate: filters.ate,
      vendedorId: "",
    };

    try {
      const [dashboardResult, recordsResult, visitasResult, atendimentosResult] = await Promise.allSettled([
        fetchRotasPlanejadasDashboard(filters),
        fetchRotasPlanejadasRecords(filters),
        fetchMercosVisitas(mercosParams),
        fetchMercosAtendimentos(mercosParams),
      ]);

      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      if (dashboardResult.status === "rejected") throw dashboardResult.reason;
      if (recordsResult.status === "rejected") throw recordsResult.reason;

      const dashboardData = dashboardResult.value;
      const recordsData = recordsResult.value;

      setDashboard({
        summary: dashboardData?.summary || {},
        rows: Array.isArray(dashboardData?.rows) ? dashboardData.rows : [],
      });
      setRecords(Array.isArray(recordsData?.rows) ? recordsData.rows : []);

      const mercosPayloads = [
        visitasResult.status === "fulfilled" ? visitasResult.value : null,
        atendimentosResult.status === "fulfilled" ? atendimentosResult.value : null,
      ];

      setMercosSellers(collectMercosSellers(...mercosPayloads));
      setMercosRows(mercosPayloads.flatMap((payload) => collectMercosRows(payload)));
    } catch (loadError) {
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      setError(loadError?.message || "Erro ao carregar o planejamento manual.");
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingSellers(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFormChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setSelectedClientSuggestion("");
    setManualClientName("");
    setBulkClientText("");
    setBulkPreview([]);
    setBulkPreviewSummary({ total: 0, matched: 0, manual: 0, duplicates: 0 });
  };

  const mergeClientsIntoForm = useCallback((clients = []) => {
    if (!Array.isArray(clients) || clients.length === 0) return 0;

    let addedCount = 0;

    setForm((current) => {
      const existing = Array.isArray(current.clientesPlanejados) ? current.clientesPlanejados : [];
      const nextClients = [...existing];

      clients.forEach((client) => {
        const clientName = String(client?.clienteNome || "").trim();
        if (!clientName) return;
        nextClients.push({
          clienteId: client?.clienteId ? String(client.clienteId).trim() : "",
          clienteNome: clientName,
        });
        addedCount += 1;
      });

      if (addedCount === 0) return current;

      return {
        ...current,
        clientesPlanejados: nextClients,
        quantidadePlanejada: String(nextClients.length),
      };
    });

    return addedCount;
  }, []);

  const handleAddSuggestedClient = () => {
    const suggestion = clientSuggestionOptions.find((item) => item.value === selectedClientSuggestion);
    if (!suggestion) return;
    mergeClientsIntoForm([{ clienteId: suggestion.clienteId, clienteNome: suggestion.clienteNome }]);
    setSelectedClientSuggestion("");
  };

  const handleAddManualClient = () => {
    const clientName = String(manualClientName || "").trim();
    if (!clientName) return;
    mergeClientsIntoForm([{ clienteId: "", clienteNome: clientName }]);
    setManualClientName("");
  };

  const clearBulkPreview = () => {
    setBulkPreview([]);
    setBulkPreviewSummary({ total: 0, matched: 0, manual: 0, duplicates: 0 });
  };

  const handleConfirmBulkClients = () => {
    if (!Array.isArray(bulkPreview) || bulkPreview.length === 0) {
      setError("Nenhuma pré-visualização disponível para confirmar.");
      setMessage("");
      return;
    }

    const clientsToAdd = bulkPreview.filter((item) => !item.isDuplicate).map((item) => ({
      clienteId: item.clienteId || "",
      clienteNome: item.clienteNome,
    }));

    const added = mergeClientsIntoForm(clientsToAdd);
    const duplicates = bulkPreviewSummary.duplicates + Math.max(0, clientsToAdd.length - added);

    setBulkClientText("");
    clearBulkPreview();
    setError("");
    setMessage(`Adição confirmada: ${added} cliente(s) incluído(s). ${bulkPreviewSummary.matched} reconhecido(s) pelo Mercos, ${bulkPreviewSummary.manual} inserido(s) manualmente${duplicates > 0 ? ` e ${duplicates} duplicado(s) ignorado(s)` : ""}.`);
  };

  const handleAddBulkClients = () => {
    const lines = parseBulkClientLines(bulkClientText);
    if (lines.length === 0) {
      setError("Cole ao menos um cliente por linha para processar a lista.");
      setMessage("");
      return;
    }

    let matched = 0;
    let manual = 0;

    const clientsToAdd = lines.map((line) => {
      const original = String(line || "").trim();
      const suggestion = findBestClientSuggestion(original, clientSuggestionOptions);
      if (suggestion) {
        matched += 1;
        return {
          clienteId: suggestion.clienteId || "",
          clienteNome: suggestion.clienteNome || suggestion.label || original,
        };
      }

      manual += 1;
      return {
        clienteId: "",
        clienteNome: original,
      };
    });

    const added = mergeClientsIntoForm(clientsToAdd);

    setBulkClientText("");
    setError("");
    setMessage(`Lista processada: ${added} cliente(s) adicionados. ${matched} reconhecido(s) pelo Mercos e ${manual} inserido(s) manualmente.`);
  };

  const handleRemoveClient = (index) => {
    setForm((current) => {
      const nextClients = (current.clientesPlanejados || []).filter((_, itemIndex) => itemIndex !== index);
      return {
        ...current,
        clientesPlanejados: nextClients,
        quantidadePlanejada: nextClients.length > 0 ? String(nextClients.length) : "",
      };
    });
  };

  const applyRouteToForm = useCallback((row) => {
    setEditingId(row.id);
    setForm({
      base: row.base || "A",
      dataRota: String(row.dataRota || "").slice(0, 10),
      vendedorNome: row.vendedorNome || "",
      nomeRoteiro: row.nomeRoteiro || "",
      quantidadePlanejada: String(row.quantidadePlanejada || ""),
      status: row.status || "ativo",
      observacao: row.observacao || "",
      clientesPlanejados: Array.isArray(row.clientesPlanejados)
        ? row.clientesPlanejados.map((client) => ({
            clienteId: client.clienteId || "",
            clienteNome: client.clienteNome || "",
          }))
        : [],
    });
    setSelectedClientSuggestion("");
    setManualClientName("");
    setBulkClientText("");
    clearBulkPreview();
    setTab("cadastro");
    setMessage("");
    setError("");
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        ...form,
        quantidadePlanejada: Number(form.quantidadePlanejada || 0),
        clientesPlanejados: Array.isArray(form.clientesPlanejados) ? form.clientesPlanejados : [],
      };

      if (editingId) {
        await updateRotaPlanejada(editingId, payload);
        setMessage("Cadastro atualizado com sucesso. O sistema preservou a versão anterior da rota no histórico.");
      } else {
        await createRotaPlanejada(payload);
        setMessage("Cadastro criado com sucesso.");
      }

      resetForm();
      await load();
      setTab("comparativo");
    } catch (saveError) {
      setError(saveError?.message || "Erro ao salvar o cadastro manual.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = useCallback((row) => {
    applyRouteToForm(row);
  }, [applyRouteToForm]);

  const handleEditCurrentFromHistory = useCallback(async (row) => {
    const currentRouteId = Number(row?.sourceRouteId || row?.id || 0);
    if (!currentRouteId) {
      setError("Não foi possível localizar o roteiro atual para edição.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const currentRoute = await fetchRotaPlanejadaById(currentRouteId);
      applyRouteToForm(currentRoute);
      setMessage("Você está editando a versão atual do roteiro. O histórico antigo continuará salvo.");
    } catch (editError) {
      setError(editError?.message || "Erro ao carregar a versão atual do roteiro.");
    } finally {
      setSaving(false);
    }
  }, [applyRouteToForm]);

  const handleOpenHistory = useCallback(async (row) => {
    const currentRouteId = Number(row?.sourceRouteId || row?.id || 0);
    if (!currentRouteId) {
      setHistoryModalRoute(row || null);
      setHistoryVersions([]);
      setHistoryError("Não foi possível localizar o histórico desse roteiro.");
      return;
    }

    setHistoryModalRoute(row || null);
    setHistoryVersions([]);
    setHistoryError("");
    setHistoryLoading(true);

    try {
      const response = await fetchRotaPlanejadaHistory(currentRouteId);
      setHistoryVersions(Array.isArray(response?.versions) ? response.versions : []);
    } catch (historyLoadError) {
      setHistoryError(historyLoadError?.message || "Erro ao carregar o histórico do roteiro.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleCloseHistory = useCallback(() => {
    setHistoryModalRoute(null);
    setHistoryVersions([]);
    setHistoryError("");
    setHistoryLoading(false);
  }, []);

  const handleDelete = async (row) => {
    const routeId = Number(row?.sourceRouteId || row?.id || 0);
    if (!routeId) {
      setError("Não foi possível identificar o roteiro para exclusão.");
      return;
    }

    const confirmed = window.confirm(`Deseja realmente excluir o roteiro "${row.nomeRoteiro}"?`);
    if (!confirmed) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await deleteRotaPlanejada(routeId);
      if (editingId === routeId) resetForm();
      setMessage("Cadastro excluído com sucesso. A última versão ficou salva no histórico.");
      await load();
    } catch (deleteError) {
      setError(deleteError?.message || "Erro ao excluir o cadastro.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHistory = async (row) => {
    const historyId = Number(row?.historyId || 0);
    const routeId = Number(row?.sourceRouteId || row?.id || 0);
    const deleteId = historyId || routeId;

    if (!deleteId) {
      setError("Não foi possível identificar o histórico para exclusão.");
      return;
    }

    const confirmed = window.confirm(`Deseja realmente apagar esse histórico do roteiro "${row.nomeRoteiro}"? A rota atual não será apagada.`);
    if (!confirmed) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await deleteRotaPlanejadaHistory(deleteId, { byHistoryId: Boolean(historyId) });
      if (historyModalRoute && routeId && Number(historyModalRoute?.sourceRouteId || historyModalRoute?.id || 0) === routeId) {
        handleCloseHistory();
      }
      setMessage("Histórico do roteiro apagado com sucesso. A rota atual foi mantida.");
      await load();
    } catch (deleteError) {
      setError(deleteError?.message || "Erro ao apagar o histórico do roteiro.");
    } finally {
      setSaving(false);
    }
  };

  const filterSellerOptions = useMemo(() => {
    const options = mercosSellers.map((item) => ({ value: item.value, label: item.label }));
    const currentValue = String(filters.vendedorNome || "").trim();
    if (currentValue && !options.some((option) => option.value === currentValue)) {
      options.unshift({ value: currentValue, label: `${currentValue} (manual)` });
    }
    return [{ value: "", label: loadingSellers ? "Carregando vendedores..." : "Todos os vendedores" }, ...options];
  }, [filters.vendedorNome, loadingSellers, mercosSellers]);

  const cadastroSellerOptions = useMemo(() => {
    const options = mercosSellers
      .filter((item) => item.bases.length === 0 || item.bases.includes(form.base))
      .map((item) => ({ value: item.value, label: item.label }));

    const currentValue = String(form.vendedorNome || "").trim();
    if (currentValue && !options.some((option) => option.value === currentValue)) {
      options.unshift({ value: currentValue, label: `${currentValue} (manual)` });
    }

    return [{ value: "", label: loadingSellers ? "Carregando vendedores..." : "Selecionar vendedor" }, ...options];
  }, [form.base, form.vendedorNome, loadingSellers, mercosSellers]);

  const clientSuggestionOptions = useMemo(
    () => buildClientSuggestions(mercosRows, {
      base: form.base,
      dataRota: form.dataRota,
      vendedorNome: form.vendedorNome,
    }),
    [form.base, form.dataRota, form.vendedorNome, mercosRows]
  );

  const summary = dashboard.summary || {};

  return (
    <div className="min-h-screen bg-[#fff7ed]">
      <NavigationHeader />

      <main className="mx-auto max-w-7xl px-3 pb-10 pt-20 sm:px-6 sm:pt-24">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#3b1d0f]">Roteiros Planejados - Controle Manual</h1>
            <p className="text-sm text-[#6b3a2a]">
              Solução 2: além do total planejado, agora você pode cadastrar os clientes do roteiro e comparar exatamente quem foi visitado e quem ficou faltando.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={load} disabled={loading || saving}>
              <span className="inline-flex items-center gap-2">
                <Icon name="RefreshCw" size={16} className={loading ? "animate-spin" : ""} />
                {loading ? "Atualizando..." : "Atualizar"}
              </span>
            </Button>
          </div>
        </div>

        {message ? <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">{message}</div> : null}
        {error ? <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">{error}</div> : null}

        <div className="mt-6 rounded-2xl border border-[#f1d6c6] bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div>
              <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Empresa</div>
              <Select
                value={filters.base}
                onChange={(value) => setFilters((current) => ({ ...current, base: value || "ALL" }))}
                options={baseOptions}
              />
            </div>

            <div>
              <Input type="date" label="De" value={filters.de} onChange={(e) => setFilters((current) => ({ ...current, de: e.target.value }))} />
            </div>

            <div>
              <Input type="date" label="Até" value={filters.ate} onChange={(e) => setFilters((current) => ({ ...current, ate: e.target.value }))} />
            </div>

            <div>
              <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Status</div>
              <Select
                value={filters.status}
                onChange={(value) => setFilters((current) => ({ ...current, status: value || "" }))}
                options={statusOptions}
              />
            </div>

            <div>
              <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Vendedor</div>
              <Select
                value={filters.vendedorNome}
                onChange={(value) => setFilters((current) => ({ ...current, vendedorNome: value || "" }))}
                options={filterSellerOptions}
                searchable
                clearable
                disabled={loadingSellers}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <SummaryCard title="Rotas planejadas" value={summary.totalRotas || 0} subtitle="Cadastros no filtro atual" icon="Route" />
          <SummaryCard title="Histórico preservado" value={summary.totalHistoricos || 0} subtitle="Versões antigas mantidas" icon="History" />
          <SummaryCard title="Total planejado" value={summary.totalPlanejado || 0} subtitle="Clientes previstos nos roteiros" icon="Target" />
          <SummaryCard title="Total realizado" value={summary.totalRealizado || 0} subtitle="Clientes visitados no Mercos" icon="CheckCircle2" />
          <SummaryCard title="Total faltante" value={summary.totalFaltante || 0} subtitle="Clientes não visitados" icon="AlertTriangle" />
          <SummaryCard title="Execução" value={formatPercent(summary.taxaExecucao || 0)} subtitle="Percentual geral" icon="BarChart3" />
        </div>

        <div className="mt-6 rounded-2xl border border-[#f1d6c6] bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setTab("comparativo")}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "comparativo" ? "bg-[#5b2d16] text-white" : "border border-[#f1d6c6] bg-[#fff7ed] text-[#6b3a2a]"}`}
            >
              Comparativo
            </button>
            <button
              type="button"
              onClick={() => setTab("cadastro")}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition ${tab === "cadastro" ? "bg-[#5b2d16] text-white" : "border border-[#f1d6c6] bg-[#fff7ed] text-[#6b3a2a]"}`}
            >
              Cadastro manual
            </button>
          </div>

          {tab === "comparativo" ? (
            <div className="overflow-x-auto">
              <table className="responsive-card-table min-w-full text-sm">
                <thead>
                  <tr className="border-b border-[#f1d6c6] text-left text-xs uppercase tracking-wide text-[#6b3a2a]">
                    <th className="py-2 pr-4">Empresa</th>
                    <th className="py-2 pr-4">Data</th>
                    <th className="py-2 pr-4">Vendedor</th>
                    <th className="py-2 pr-4">Roteiro</th>
                    <th className="py-2 pr-4">Planejado</th>
                    <th className="py-2 pr-4">Visitado</th>
                    <th className="py-2 pr-4">Faltante</th>
                    <th className="py-2 pr-4">Execução</th>
                    <th className="py-2 pr-4">Clientes</th>
                    <th className="py-2 pr-0">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.rows.length === 0 ? (
                    <tr className="responsive-card-table-empty">
                      <td className="py-4 text-[#6b3a2a]" colSpan={10}>
                        Nenhum cadastro encontrado para o filtro selecionado.
                      </td>
                    </tr>
                  ) : (
                    dashboard.rows.map((row) => {
                      const rowIdentity = row.rowKey || `route-${row.id}`;
                      const expanded = expandedRowId === rowIdentity;
                      const clientsCount = Array.isArray(row.clientesPlanejados) ? row.clientesPlanejados.length : 0;
                      return (
                        <React.Fragment key={rowIdentity}>
                          <tr className="border-b border-[#f7e7dc] align-top">
                            <td data-label="Empresa" className="py-2 pr-4 text-[#3b1d0f]">{formatBaseLabel(row.base)}</td>
                            <td data-label="Data" className="py-2 pr-4 text-[#3b1d0f]">
                              <div>{formatDateBR(row.dataRota)}</div>
                              {row.isHistorico ? <div className="mt-1 text-[11px] font-medium text-amber-700">Versão histórica</div> : null}
                            </td>
                            <td data-label="Vendedor" className="py-2 pr-4 text-[#3b1d0f]">{row.vendedorNome || "---"}</td>
                            <td data-label="Roteiro" className="py-2 pr-4 text-[#3b1d0f]">
                              <div>{row.nomeRoteiro || "---"}</div>
                              {row.isHistorico ? (
                                <div className="mt-1 text-[11px] text-[#8a5a46]">
                                  {getHistoryEventLabel(row.historyEvent)} em {formatDateTimeBR(row.historyOccurredEm)}
                                </div>
                              ) : null}
                            </td>
                            <td data-label="Planejado" className="py-2 pr-4 text-[#3b1d0f]">{row.quantidadePlanejada || 0}</td>
                            <td data-label="Visitado" className="py-2 pr-4 text-[#3b1d0f]">{row.quantidadeRealizada || 0}</td>
                            <td data-label="Faltante" className="py-2 pr-4 text-[#3b1d0f]">{row.quantidadeFaltante || 0}</td>
                            <td data-label="Execução" className="py-2 pr-4 text-[#3b1d0f]">{formatPercent(row.percentualExecucao || 0)}</td>
                            <td data-label="Clientes" className="py-2 pr-4 text-[#3b1d0f]">
                              {row.usaListaClientes ? `${clientsCount} cadastrados` : "Sem lista"}
                            </td>
                            <td data-label="Ações" className="py-2 text-[#3b1d0f]">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => setExpandedRowId(expanded ? null : rowIdentity)}
                                  className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#6b3a2a]"
                                >
                                  {expanded ? "Fechar" : "Detalhes"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleOpenHistory(row)}
                                  className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#6b3a2a]"
                                >
                                  Histórico
                                </button>
                                {row.isHistorico ? (
                                  <>
                                    {row.currentRouteExists !== false ? (
                                      <button
                                        type="button"
                                        onClick={() => handleEditCurrentFromHistory(row)}
                                        className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#6b3a2a]"
                                      >
                                        Editar atual
                                      </button>
                                    ) : (
                                      <span className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#8a5a46]">
                                        Somente histórico
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteHistory(row)}
                                      disabled={saving}
                                      className="rounded-lg border border-red-200 px-3 py-1 text-xs font-medium text-red-700 disabled:opacity-60"
                                    >
                                      Apagar histórico
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleEdit(row)}
                                    className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#6b3a2a]"
                                  >
                                    Editar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {expanded ? (
                            <tr className="responsive-card-table-expanded border-b border-[#f7e7dc] bg-[#fffaf5]">
                              <td className="py-4" colSpan={10}>
                                {row.isHistorico ? (
                                  <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                    Essa é uma versão antiga preservada automaticamente. Mesmo após editar a data ou outros campos, esse estado continua disponível para consulta.
                                  </div>
                                ) : null}
                                {row.usaListaClientes ? (
                                  <div className="grid gap-4 lg:grid-cols-2">
                                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                                      <div className="mb-2 text-sm font-semibold text-emerald-800">
                                        Clientes visitados ({row.clientesVisitados?.length || 0})
                                      </div>
                                      {Array.isArray(row.clientesVisitados) && row.clientesVisitados.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                          {row.clientesVisitados.map((cliente) => (
                                            <span key={`${cliente.clienteId || cliente.clienteNome}-${cliente.matchKey || ""}`} className="rounded-full bg-white px-3 py-1 text-xs text-emerald-900 shadow-sm">
                                              {cliente.clienteNome}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-sm text-emerald-900">Nenhum cliente planejado foi visitado nesse roteiro.</div>
                                      )}
                                    </div>

                                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                                      <div className="mb-2 text-sm font-semibold text-red-800">
                                        Clientes não visitados ({row.clientesNaoVisitados?.length || 0})
                                      </div>
                                      {Array.isArray(row.clientesNaoVisitados) && row.clientesNaoVisitados.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                          {row.clientesNaoVisitados.map((cliente) => (
                                            <span key={`${cliente.clienteId || cliente.clienteNome}-nao-visitado`} className="rounded-full bg-white px-3 py-1 text-xs text-red-900 shadow-sm">
                                              {cliente.clienteNome}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-sm text-red-900">Nenhum cliente faltante nesse roteiro.</div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                    Esse cadastro ainda está no modelo da solução 1. Para comparar cliente por cliente, edite o roteiro e cadastre a lista de clientes planejados.
                                  </div>
                                )}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[460px,minmax(0,1fr)]">
              <form onSubmit={handleSubmit} className="rounded-2xl border border-[#f7e7dc] bg-[#fffaf5] p-4">
                <div className="mb-4">
                  <div className="text-sm font-semibold text-[#3b1d0f]">
                    {editingId ? "Editar roteiro completo" : "Novo roteiro completo"}
                  </div>
                  <div className="text-xs text-[#6b3a2a]">
                    Aqui você informa vendedor, roteiro e também a lista dos clientes planejados para comparar exatamente quais foram visitados e quais ficaram faltando.
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Empresa</div>
                    <Select value={form.base} onChange={(value) => handleFormChange("base", value || "A")} options={cadastroBaseOptions} />
                  </div>

                  <Input label="Data da rota" type="date" value={form.dataRota} onChange={(e) => handleFormChange("dataRota", e.target.value)} required />

                  <div>
                    <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Vendedor</div>
                    <Select
                      value={form.vendedorNome}
                      onChange={(value) => handleFormChange("vendedorNome", value || "")}
                      options={cadastroSellerOptions}
                      searchable
                      clearable
                      disabled={loadingSellers}
                    />
                    <Input className="mt-2" placeholder="Ou digite manualmente" value={form.vendedorNome} onChange={(e) => handleFormChange("vendedorNome", e.target.value)} required />
                  </div>

                  <Input label="Nome do roteiro" value={form.nomeRoteiro} onChange={(e) => handleFormChange("nomeRoteiro", e.target.value)} required />

                  <Input
                    label="Quantidade planejada"
                    type="number"
                    min="0"
                    value={form.clientesPlanejados.length > 0 ? String(form.clientesPlanejados.length) : form.quantidadePlanejada}
                    onChange={(e) => handleFormChange("quantidadePlanejada", e.target.value)}
                    description={form.clientesPlanejados.length > 0 ? "Esse total está sendo calculado automaticamente pela lista de clientes." : "Se você não cadastrar clientes, a comparação continuará só por quantidade."}
                    readOnly={form.clientesPlanejados.length > 0}
                    required
                  />

                  <div>
                    <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Status</div>
                    <Select value={form.status} onChange={(value) => handleFormChange("status", value || "ativo")} options={statusOptions.filter((option) => option.value)} />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-[#6b3a2a]">Observação</label>
                    <textarea
                      className="min-h-[90px] w-full rounded-md border border-[#f1d6c6] bg-white px-3 py-2 text-sm text-[#3b1d0f] outline-none focus:border-[#d27e2d]"
                      value={form.observacao}
                      onChange={(e) => handleFormChange("observacao", e.target.value)}
                      placeholder="Campo opcional"
                    />
                  </div>

                  <div className="rounded-2xl border border-[#f1d6c6] bg-white p-3">
                    <div className="mb-2 text-sm font-semibold text-[#3b1d0f]">Clientes do roteiro</div>
                    <div className="text-xs text-[#6b3a2a]">
                      Use os clientes sugeridos pelo Mercos com base na data, empresa e vendedor escolhidos ou adicione nomes manualmente.
                    </div>

                    <div className="mt-3 grid gap-3">
                      <div>
                        <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Sugestão do Mercos</div>
                        <Select
                          value={selectedClientSuggestion}
                          onChange={(value) => setSelectedClientSuggestion(value || "")}
                          options={[
                            { value: "", label: clientSuggestionOptions.length > 0 ? "Selecionar cliente sugerido" : "Nenhuma sugestão encontrada" },
                            ...clientSuggestionOptions.map((item) => ({ value: item.value, label: item.label })),
                          ]}
                          searchable
                          clearable
                          disabled={!form.vendedorNome || clientSuggestionOptions.length === 0}
                        />
                        <div className="mt-2 flex justify-stretch sm:justify-end">
                          <button
                            type="button"
                            onClick={handleAddSuggestedClient}
                            className="w-full rounded-lg bg-[#d27e2d] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50 sm:w-auto"
                            disabled={!selectedClientSuggestion}
                          >
                            Adicionar sugestão
                          </button>
                        </div>
                      </div>

                      <div>
                        <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Adicionar manualmente</div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <input
                            className="h-10 flex-1 rounded-md border border-[#f1d6c6] bg-white px-3 text-sm text-[#3b1d0f] outline-none focus:border-[#d27e2d]"
                            value={manualClientName}
                            onChange={(e) => setManualClientName(e.target.value)}
                            placeholder="Ex.: MERCADO CENTRAL LTDA"
                          />
                          <button
                            type="button"
                            onClick={handleAddManualClient}
                            className="w-full rounded-lg border border-[#f1d6c6] px-3 py-2 text-xs font-semibold text-[#6b3a2a] disabled:opacity-50 sm:w-auto"
                            disabled={!String(manualClientName || "").trim()}
                          >
                            Adicionar
                          </button>
                        </div>
                      </div>

                      <div>
                        <div className="mb-1 text-xs font-medium text-[#6b3a2a]">Adicionar em lote</div>
                        <div className="text-[11px] text-[#8a5a46]">
                          Cole um cliente por linha. O sistema tenta reconhecer pelo Mercos e, quando não encontrar, adiciona o nome manualmente.
                        </div>
                        <div className="mt-2 space-y-2">
                          <textarea
                            className="min-h-[120px] w-full rounded-md border border-[#f1d6c6] bg-white px-3 py-2 text-sm text-[#3b1d0f] outline-none focus:border-[#d27e2d]"
                            value={bulkClientText}
                            onChange={(e) => setBulkClientText(e.target.value)}
                            placeholder={`Ex.: TOCA DA FEIJOADA BAR E RESTAURANTE LTDA
BANCA DE JORNAL - SF TAMOIO
MARAVISTA COMERCIO DE ALIMENTOS LTDA`}
                          />
                          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                setBulkClientText("");
                                clearBulkPreview();
                              }}
                              className="w-full rounded-lg border border-[#f1d6c6] px-3 py-2 text-xs font-semibold text-[#6b3a2a] sm:w-auto"
                              disabled={!String(bulkClientText || "").trim() && bulkPreview.length === 0}
                            >
                              Limpar lista
                            </button>
                            <button
                              type="button"
                              onClick={handleAddBulkClients}
                              className="w-full rounded-lg bg-[#d27e2d] px-3 py-2 text-xs font-semibold text-white disabled:opacity-50 sm:w-auto"
                              disabled={!String(bulkClientText || "").trim()}
                            >
                              Processar lista
                            </button>
                          </div>

                          {bulkPreview.length > 0 ? (
                            <div className="rounded-2xl border border-[#f1d6c6] bg-[#fffaf5] p-3">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                  <div className="text-sm font-semibold text-[#3b1d0f]">Pré-visualização do resultado</div>
                                  <div className="mt-1 text-xs text-[#6b3a2a]">
                                    {bulkPreviewSummary.total} cliente(s), {bulkPreviewSummary.matched} reconhecido(s) pelo Mercos, {bulkPreviewSummary.manual} manual(is) e {bulkPreviewSummary.duplicates} duplicado(s).
                                  </div>
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row">
                                  <button
                                    type="button"
                                    onClick={clearBulkPreview}
                                    className="w-full rounded-lg border border-[#f1d6c6] px-3 py-2 text-xs font-semibold text-[#6b3a2a] sm:w-auto"
                                  >
                                    Cancelar prévia
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleConfirmBulkClients}
                                    className="w-full rounded-lg bg-[#d27e2d] px-3 py-2 text-xs font-semibold text-white sm:w-auto"
                                  >
                                    Confirmar adição
                                  </button>
                                </div>
                              </div>

                              <div className="mt-3 max-h-52 space-y-2 overflow-auto pr-1">
                                {bulkPreview.map((item, index) => (
                                  <div key={`${item.key}-${index}`} className="flex flex-col gap-2 rounded-xl border border-[#f1d6c6] bg-white px-3 py-2 text-sm text-[#3b1d0f] sm:flex-row sm:items-start sm:justify-between">
                                    <div className="min-w-0 flex-1">
                                      <div className="break-words font-medium leading-snug whitespace-normal">{item.clienteNome}</div>
                                      <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                                        <span className={`rounded-full px-2 py-0.5 ${item.foundInMercos ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                          {item.foundInMercos ? "Reconhecido no Mercos" : "Adição manual"}
                                        </span>
                                        {item.isDuplicate ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">Duplicado</span> : null}
                                        {item.clienteId ? <span className="rounded-full bg-[#fff3e8] px-2 py-0.5 text-[#8a5a46]">ID Mercos: {item.clienteId}</span> : null}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 text-xs font-medium text-[#6b3a2a]">
                          Clientes cadastrados ({form.clientesPlanejados.length})
                        </div>
                        {form.clientesPlanejados.length === 0 ? (
                          <div className="rounded-xl border border-dashed border-[#f1d6c6] px-3 py-4 text-sm text-[#6b3a2a]">
                            Nenhum cliente adicionado ainda. Você pode salvar sem lista para continuar no modelo simples ou cadastrar os clientes para ativar a comparação completa.
                          </div>
                        ) : (
                          <div className="max-h-56 space-y-2 overflow-auto overflow-x-hidden pr-1">
                            {form.clientesPlanejados.map((cliente, index) => (
                              <div key={`${cliente.clienteId || cliente.clienteNome}-${index}`} className="flex flex-col gap-2 rounded-xl border border-[#f1d6c6] bg-[#fffaf5] px-3 py-2 text-sm text-[#3b1d0f] sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0 flex-1">
                                  <div className="break-words font-medium leading-snug whitespace-normal">{cliente.clienteNome}</div>
                                  {cliente.clienteId ? <div className="text-xs text-[#6b3a2a]">ID Mercos: {cliente.clienteId}</div> : null}
                                </div>
                                <button type="button" onClick={() => handleRemoveClient(index)} className="self-start rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700 sm:ml-3 sm:self-center">
                                  Remover
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button type="submit" disabled={saving}>
                    {saving ? "Salvando..." : editingId ? "Atualizar cadastro" : "Salvar cadastro"}
                  </Button>
                  <button type="button" onClick={resetForm} className="rounded-xl border border-[#f1d6c6] px-4 py-2 text-sm text-[#6b3a2a]" disabled={saving}>
                    Limpar
                  </button>
                </div>
              </form>

              <div className="overflow-x-auto rounded-2xl border border-[#f7e7dc] bg-white p-4">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#f1d6c6] text-left text-xs uppercase tracking-wide text-[#6b3a2a]">
                      <th className="py-2 pr-4">Data</th>
                      <th className="py-2 pr-4">Empresa</th>
                      <th className="py-2 pr-4">Vendedor</th>
                      <th className="py-2 pr-4">Roteiro</th>
                      <th className="py-2 pr-4">Qtd.</th>
                      <th className="py-2 pr-4">Clientes</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-0">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 ? (
                      <tr>
                        <td className="py-4 text-[#6b3a2a]" colSpan={8}>
                          Nenhum cadastro manual encontrado.
                        </td>
                      </tr>
                    ) : (
                      records.map((row) => (
                        <tr key={row.rowKey || row.id} className="border-b border-[#f7e7dc] align-top">
                          <td className="py-2 pr-4 text-[#3b1d0f]">
                            <div>{formatDateBR(row.dataRota)}</div>
                            {row.isHistorico ? <div className="mt-1 text-[11px] font-medium text-amber-700">Histórico preservado</div> : null}
                          </td>
                          <td className="py-2 pr-4 text-[#3b1d0f]">{formatBaseLabel(row.base)}</td>
                          <td className="py-2 pr-4 text-[#3b1d0f]">{row.vendedorNome || "---"}</td>
                          <td className="py-2 pr-4 text-[#3b1d0f]">
                            <div>{row.nomeRoteiro || "---"}</div>
                            {row.isHistorico ? (
                              <div className="mt-1 text-[11px] text-[#8a5a46]">
                                {getHistoryEventLabel(row.historyEvent)} em {formatDateTimeBR(row.historyOccurredEm)}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-2 pr-4 text-[#3b1d0f]">{row.quantidadePlanejada || 0}</td>
                          <td className="py-2 pr-4 text-[#3b1d0f]">{Array.isArray(row.clientesPlanejados) ? row.clientesPlanejados.length : 0}</td>
                          <td className="py-2 pr-4 text-[#3b1d0f]">{row.status || "---"}</td>
                          <td className="py-2 text-[#3b1d0f]">
                            <div className="flex flex-wrap gap-2">
                              <button type="button" onClick={() => handleOpenHistory(row)} className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#6b3a2a]">
                                Histórico
                              </button>
                              {row.isHistorico ? (
                                <>
                                  {row.currentRouteExists !== false ? (
                                    <button type="button" onClick={() => handleEditCurrentFromHistory(row)} className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#6b3a2a]">
                                      Editar atual
                                    </button>
                                  ) : (
                                    <span className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#8a5a46]">
                                      Somente histórico
                                    </span>
                                  )}
                                  <button type="button" onClick={() => handleDeleteHistory(row)} disabled={saving} className="rounded-lg border border-red-200 px-3 py-1 text-xs font-medium text-red-700 disabled:opacity-60">
                                    Apagar histórico
                                  </button>
                                </>
                              ) : (
                                <button type="button" onClick={() => handleEdit(row)} className="rounded-lg border border-[#f1d6c6] px-3 py-1 text-xs font-medium text-[#6b3a2a]">
                                  Editar
                                </button>
                              )}
                              {!row.isHistorico ? (
                                <button type="button" onClick={() => handleDelete(row)} className="rounded-lg border border-red-200 px-3 py-1 text-xs font-medium text-red-700">
                                  Excluir
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {historyModalRoute ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
            <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-[#f1d6c6] px-6 py-4">
                <div>
                  <div className="text-lg font-semibold text-[#3b1d0f]">Histórico do roteiro</div>
                  <div className="text-sm text-[#6b3a2a]">
                    {historyModalRoute.nomeRoteiro || "Roteiro"} • {historyModalRoute.vendedorNome || "Sem vendedor"}
                  </div>
                </div>
                <button type="button" onClick={handleCloseHistory} className="rounded-xl border border-[#f1d6c6] px-3 py-2 text-sm text-[#6b3a2a]">
                  Fechar
                </button>
              </div>

              <div className="max-h-[calc(90vh-88px)] overflow-y-auto px-6 py-5">
                {historyLoading ? (
                  <div className="rounded-2xl border border-[#f1d6c6] bg-[#fffaf5] p-4 text-sm text-[#6b3a2a]">Carregando histórico...</div>
                ) : historyError ? (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{historyError}</div>
                ) : historyVersions.length === 0 ? (
                  <div className="rounded-2xl border border-[#f1d6c6] bg-[#fffaf5] p-4 text-sm text-[#6b3a2a]">Ainda não existe histórico salvo para esse roteiro.</div>
                ) : (
                  <div className="space-y-4">
                    {historyVersions.map((version, index) => (
                      <div key={version.versionKey || index} className="rounded-2xl border border-[#f1d6c6] bg-[#fffaf5] p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${version.isCurrent ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                {version.isCurrent ? "Versão atual" : getHistoryEventLabel(version.event)}
                              </span>
                              <span className="text-xs text-[#8a5a46]">{formatDateTimeBR(version.occurredEm)}</span>
                            </div>
                            <div className="mt-2 text-base font-semibold text-[#3b1d0f]">{version.route?.nomeRoteiro || "Roteiro sem nome"}</div>
                            <div className="mt-1 text-sm text-[#6b3a2a]">
                              {formatBaseLabel(version.route?.base)} • {formatDateBR(version.route?.dataRota)} • {version.route?.vendedorNome || "Sem vendedor"}
                            </div>
                          </div>
                          {!version.isCurrent && historyVersions.some((item) => item.isCurrent) ? (
                            <button
                              type="button"
                              onClick={() => handleEditCurrentFromHistory(historyModalRoute)}
                              className="rounded-xl border border-[#f1d6c6] px-3 py-2 text-xs font-semibold text-[#6b3a2a]"
                            >
                              Editar versão atual
                            </button>
                          ) : null}
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-4">
                          <div className="rounded-xl border border-[#f1d6c6] bg-white px-3 py-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8a5a46]">Quantidade</div>
                            <div className="mt-1 text-sm font-semibold text-[#3b1d0f]">{version.route?.quantidadePlanejada || 0}</div>
                          </div>
                          <div className="rounded-xl border border-[#f1d6c6] bg-white px-3 py-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8a5a46]">Status</div>
                            <div className="mt-1 text-sm font-semibold text-[#3b1d0f]">{version.route?.status || "---"}</div>
                          </div>
                          <div className="rounded-xl border border-[#f1d6c6] bg-white px-3 py-2 md:col-span-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8a5a46]">Alterações registradas</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {Array.isArray(version.changedFields) && version.changedFields.length > 0 ? (
                                version.changedFields.map((field) => (
                                  <span key={field} className="rounded-full bg-[#fff3e8] px-2 py-1 text-xs text-[#8a5a46]">
                                    {getChangedFieldLabel(field)}
                                  </span>
                                ))
                              ) : (
                                <span className="text-sm text-[#6b3a2a]">Sem alterações adicionais registradas.</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {version.route?.observacao ? (
                          <div className="mt-4 rounded-xl border border-[#f1d6c6] bg-white p-3 text-sm text-[#3b1d0f]">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8a5a46]">Observação</div>
                            {version.route.observacao}
                          </div>
                        ) : null}

                        <div className="mt-4 rounded-xl border border-[#f1d6c6] bg-white p-3">
                          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#8a5a46]">
                            Clientes da versão ({Array.isArray(version.route?.clientesPlanejados) ? version.route.clientesPlanejados.length : 0})
                          </div>
                          {Array.isArray(version.route?.clientesPlanejados) && version.route.clientesPlanejados.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {version.route.clientesPlanejados.map((cliente, index) => (
                                <span key={`${cliente.clienteId || cliente.clienteNome}-${index}`} className="rounded-full bg-[#fff7ed] px-3 py-1 text-xs text-[#6b3a2a]">
                                  {cliente.clienteNome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-[#6b3a2a]">Essa versão não tinha clientes vinculados.</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
